// src/components/admin/EditarUsuario.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import useEliminarUsuario from "../../hooks/useEliminarUsuario";

const API_BASE_URL =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

// Mapeo de programas por facultad (mismo del registro)
const PROGRAMAS_POR_FACULTAD = {
  "Facultad de Arquitectura y Diseño": [
    "Diseño Audiovisual",
    "Arquitectura",
    "Diseño Industrial",
    "Especialización en Gestión de Proyectos e Innovación",
    "Especialización en Arquitectura Bioclimática",
    "Especialización en Gestión de la Construcción Sostenible",
    "Maestría en Arquitectura y Urbanismo",
  ],
  "Facultad de Ciencias Humanas, Sociales y de la Educación": [
    "Psicología",
    "Comunicación Social - Periodismo",
    "Licenciatura en Educación Religiosa",
    "Especialización en Pedagogía y Desarrollo Humano",
    "Especialización en Edumática Innovación Educativa Mediada por TIC",
    "Especialización en Gerencia de la Comunicación Corporativa",
    "Especialización en Psicología Clínica. Énfasis en Psicoterapia con niños y adolescentes",
    "Especialización en Psicología Social Comunitaria y Acción Psicosocial",
    "Especialización en Gestión Humana en las Organizaciones",
    "Maestría en Pedagogía y Desarrollo Humano",
    "Maestría en Innovación Educativa",
    "Maestría en Gestión Estratégica de la Comunicación",
    "Maestría en Memoria y Escenarios Transicionales",
    "Doctorado en Educación en Desarrollo Humano",
    "Departamento de Humanidades",
  ],
  "Facultad de Ciencias Básicas e Ingeniería": [
    "Ingeniería de Sistemas y Telecomunicaciones",
    "Ingeniería Industrial",
    "Tecnología en Desarrollo de Software",
    "Departamento de Ciencias Básicas",
  ],
  "Facultad de Ciencias Económicas y Administrativas": [
    "Administración de Empresas",
    "Negocios Internacionales",
    "Mercadeo",
    "Tecnología en Gestión de Mercadeo",
    "Especialización en Finanzas",
    "Especialización en Economía Pública y Gestión Territorial",
    "Maestría en Mercadeo",
    "Maestría en Gestión de Proyectos",
    "Maestría en Gestión del Desarrollo Regional",
    "Maestría en Finanzas",
  ],
  Otro: ["Otro"],
};

const EditarUsuario = ({ usuario, onClose }) => {
  // Estado de todos los campos
  const [nombre, setNombre] = useState(usuario?.nombre || "");
  const [apellidos, setApellidos] = useState(usuario?.apellidos || "");
  const [tipocc, setTipocc] = useState(usuario?.tipocc || "");
  const [numcc, setNumcc] = useState(usuario?.numcc || "");
  const [correo, setCorreo] = useState((usuario?.correo || "").toLowerCase());
  const [rol, setRol] = useState(usuario?.rol || "");
  const [facultad, setFacultad] = useState(usuario?.facultad || "");
  const [programa, setPrograma] = useState(usuario?.programa || "");
  const [nuevaContrasena, setNuevaContrasena] = useState("");

  // UI
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const eliminarUsuario = useEliminarUsuario();

  // Cargar datos frescos del usuario para editar
  useEffect(() => {
    if (!usuario?.id) return;
    let ignore = false;

    const fetchUsuarioParaEditar = async () => {
      try {
        const authToken = localStorage.getItem("token");
        const { data } = await axios.get(
          `${API_BASE_URL}/api/usuarios/${usuario.id}/edit`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (ignore) return;
        // Setea todos los campos con fallback vacío
        setNombre(data?.nombre || "");
        setApellidos(data?.apellidos || "");
        setTipocc(data?.tipocc || "");
        setNumcc(data?.numcc || "");
        setCorreo((data?.correo || "").toLowerCase());
        setRol(data?.rol || "");
        setFacultad(data?.facultad || "");
        setPrograma(data?.programa || "");
      } catch (err) {
        console.error("Error al obtener el usuario para editar:", err);
        setError("Error al cargar los datos del usuario");
      }
    };

    fetchUsuarioParaEditar();
    return () => {
      ignore = true;
    };
  }, [usuario?.id]);

  // Si cambia la facultad y el programa actual no pertenece, limpiamos programa
  useEffect(() => {
    if (!facultad) {
      setPrograma("");
      return;
    }
    const lista = PROGRAMAS_POR_FACULTAD[facultad] || [];
    if (programa && !lista.includes(programa)) {
      setPrograma(""); // fuerza a elegir de la nueva lista
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultad]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const authToken = localStorage.getItem("token");

      const dataToUpdate = {
        nombre: (nombre || "").trim(),
        apellidos: (apellidos || "").trim(),
        tipocc,
        numcc,
        correo: (correo || "").toLowerCase().trim(),
        rol, // exacto como backend espera
        facultad,
        programa,
      };

      if (nuevaContrasena) {
        dataToUpdate.contrasena = nuevaContrasena; // el backend hace el hash
      }

      await axios.put(
        `${API_BASE_URL}/api/usuarios/${usuario.id}`,
        dataToUpdate,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      onClose();
    } catch (err) {
      setError("Error al actualizar el usuario");
      console.error("Error al actualizar el usuario:", err);
    }
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  // --- Confirmación de borrado bonita ---
  const handleDeleteClick = () => setConfirmOpen(true);
  const closeConfirm = () => !isDeleting && setConfirmOpen(false);

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await eliminarUsuario(usuario.id);
      onClose(); // el padre refresca la lista
    } catch (e) {
      setError("Error al eliminar el usuario");
      console.error(e);
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
    }
  };

  const programas = facultad ? PROGRAMAS_POR_FACULTAD[facultad] || [] : [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
      <div className="relative top-20 mx-auto mb-[200px] p-5 border w-[28rem] shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-bold mb-4">Editar usuario</h3>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Nombre / Apellidos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="nombre"
              >
                Nombre
              </label>
              <input
                type="text"
                id="nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="apellidos"
              >
                Apellidos
              </label>
              <input
                type="text"
                id="apellidos"
                value={apellidos}
                onChange={(e) => setApellidos(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>

          {/* Tipo/Número documento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="tipocc"
              >
                Tipo de documento
              </label>
              <select
                id="tipocc"
                value={tipocc}
                onChange={(e) => setTipocc(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                required
              >
                <option value="" disabled>
                  Seleccione…
                </option>
                <option value="Cédula de Ciudadanía">
                  Cédula de Ciudadanía
                </option>
                <option value="Tarjeta de Identidad">
                  Tarjeta de Identidad
                </option>
                <option value="Cédula de Extranjería">
                  Cédula de Extranjería
                </option>
              </select>
            </div>

            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="numcc"
              >
                Número de documento
              </label>
              <input
                type="number"
                id="numcc"
                value={numcc}
                onChange={(e) => setNumcc(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>

          {/* Correo */}
          <div>
            <label
              className="block text-gray-700 text-sm font-bold mb-1"
              htmlFor="correo"
            >
              Correo
            </label>
            <input
              type="email"
              id="correo"
              value={correo}
              onChange={(e) => setCorreo((e.target.value || "").toLowerCase())}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          {/* Nueva contraseña */}
          <div>
            <label
              className="block text-gray-700 text-sm font-bold mb-1"
              htmlFor="nuevaContrasena"
            >
              Nueva contraseña (dejar en blanco para no cambiar)
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="nuevaContrasena"
                value={nuevaContrasena}
                onChange={(e) => setNuevaContrasena(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                onClick={togglePasswordVisibility}
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Rol */}
          <div>
            <label
              className="block text-gray-700 text-sm font-bold mb-1"
              htmlFor="rol"
            >
              Rol
            </label>
            <select
              id="rol"
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
              required
            >
              <option value="" disabled>
                Seleccione rol…
              </option>
              <option value="Estudiante">Estudiante</option>
              <option value="Docente de planta">Docente de planta</option>
              <option value="Docente de cátedra">Docente de cátedra</option>
              <option value="Administrativo">Administrativo</option>
              <option value="Administrador">Administrador</option>
            </select>
          </div>

          {/* Facultad / Programa dependiente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="facultad"
              >
                Facultad
              </label>
              <select
                id="facultad"
                value={facultad}
                onChange={(e) => setFacultad(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                required
              >
                <option value="" disabled>
                  Seleccione la facultad
                </option>
                <option value="Facultad de Arquitectura y Diseño">
                  Facultad de Arquitectura y Diseño
                </option>
                <option value="Facultad de Ciencias Humanas, Sociales y de la Educación">
                  Facultad de Ciencias Humanas, Sociales y de la Educación
                </option>
                <option value="Facultad de Ciencias Básicas e Ingeniería">
                  Facultad de Ciencias Básicas e Ingeniería
                </option>
                <option value="Facultad de Ciencias Económicas y Administrativas">
                  Facultad de Ciencias Económicas y Administrativas
                </option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            <div>
              <label
                className="block text-gray-700 text-sm font-bold mb-1"
                htmlFor="programa"
              >
                Programa
              </label>
              <select
                id="programa"
                value={facultad ? programa || "" : "Esperando facultad..."}
                onChange={(e) => setPrograma(e.target.value)}
                disabled={!facultad}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline disabled:bg-gray-100"
                required
              >
                {!facultad && (
                  <option value="Esperando facultad...">
                    Esperando facultad...
                  </option>
                )}
                {facultad &&
                  programas.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {error && <p className="text-red-500 text-xs italic">{error}</p>}

          <div className="flex items-center justify-between mt-2">
            <button
              type="submit"
              className="bg-green-700 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Guardar
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                {isDeleting ? "Eliminando..." : "Eliminar"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {/* Modal de confirmación */}
        {confirmOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            onKeyDown={(e) => e.key === "Escape" && closeConfirm()}
          >
            <div className="bg-white w-[26rem] max-w-[90vw] rounded-xl shadow-2xl p-6">
              <h4
                id="confirm-delete-title"
                className="text-lg font-semibold text-gray-900"
              >
                ¿Eliminar usuario?
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                Esta acción no se puede deshacer. El usuario será eliminado del
                sistema.
              </p>

              {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-800 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-70"
                >
                  {isDeleting ? "Eliminando..." : "Sí, eliminar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditarUsuario;
